import React, { useState } from 'react';
import { BookOpen, FileText, Users, Award, List, User, ChevronDown, ChevronUp, Heart, Briefcase, Users2 } from 'lucide-react';
import { scenarios } from '../data/scenarios';
import { personnages } from '../data/personnages';

export default function ResourcesPage() {
  const [showScenarios, setShowScenarios] = useState(false);
  const [showPersonnages, setShowPersonnages] = useState(false);

  const getNiveauColor = (niveau: string) => {
    switch (niveau) {
      case 'Débutant':
        return 'bg-green-100 text-green-800';
      case 'Intermédiaire':
        return 'bg-yellow-100 text-yellow-800';
      case 'Avancé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Médical':
        return 'bg-blue-100 text-blue-800';
      case 'Traumatique':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <main className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Ressources Pédagogiques</h1>
          <p className="mt-2 text-gray-600">
            Documentation et guides essentiels pour la formation en secourisme
          </p>
        </div>

        <div className="space-y-8">
          {/* Liste des scénarios */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <button
              onClick={() => setShowScenarios(!showScenarios)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <List className="h-6 w-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Liste complète des scénarios</h2>
              </div>
              {showScenarios ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {showScenarios && (
              <div className="mt-4 space-y-4">
                {scenarios.map((scenario) => (
                  <div key={scenario.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{scenario.titre}</h3>
                      <div className="flex space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(scenario.type)}`}>
                          {scenario.type}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNiveauColor(scenario.niveau)}`}>
                          {scenario.niveau}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{scenario.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>Durée: {scenario.duree} min</span>
                      <span>Participants: {scenario.participants}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Liste des personnages */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <button
              onClick={() => setShowPersonnages(!showPersonnages)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <Users2 className="h-6 w-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Liste des personnages</h2>
              </div>
              {showPersonnages ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {showPersonnages && (
              <div className="mt-4 space-y-6">
                {personnages.map((personnage) => (
                  <div key={personnage.id} className="bg-gray-50 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Colonne 1: Informations personnelles */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {personnage.prenom} {personnage.nom}
                        </h3>
                        <p className="text-gray-600 mb-4">{personnage.age} ans</p>
                        
                        <div className="space-y-2">
                          <div className="flex items-center text-gray-600">
                            <Briefcase className="h-4 w-4 mr-2" />
                            <span>{personnage.profession || 'Non spécifié'}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Users className="h-4 w-4 mr-2" />
                            <span>{personnage.statutMatrimonial}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <User className="h-4 w-4 mr-2" />
                            <span>{personnage.enfants} enfant(s)</span>
                          </div>
                        </div>
                      </div>

                      {/* Colonne 2: Informations médicales */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Informations médicales</h4>
                        <div className="space-y-2">
                          <p className="text-sm">
                            <span className="font-medium">Groupe sanguin:</span> {personnage.groupeSanguin || 'Non spécifié'}
                          </p>
                          {personnage.conditionsMedicales.length > 0 && (
                            <div>
                              <p className="font-medium text-sm">Conditions médicales:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {personnage.conditionsMedicales.map((condition, index) => (
                                  <li key={index}>{condition.nom}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {personnage.allergies.length > 0 && (
                            <div>
                              <p className="font-medium text-sm">Allergies:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {personnage.allergies.map((allergie, index) => (
                                  <li key={index}>{allergie}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Colonne 3: Traits et comportement */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Personnalité</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium text-sm">Traits:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {personnage.traits.map((trait, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                >
                                  {trait}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-sm">Comportements:</p>
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {personnage.comportement.map((comp, index) => (
                                <li key={index}>{comp}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Histoire du personnage */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600">{personnage.histoire}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}